/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useContext, useState } from "react";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useSnackbar } from "hooks/useSnackbar";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { Logger } from "cms/utils/log/logger";
import { useSimplicityImage } from "cms/images/hooks/use-simplicity-image";
import { TextFieldEditor } from "components/text-field-editor";
import { useAuth } from "cms/auth/hooks/use-auth";

const ImageRenamePanel = (props) => {

    const fileNameExist = props?.fileNameExist;
    const setFileNameExist = props?.setFileNameExist;
    const renameFileNameValid  = props?.renameFileNameValid;
    const setRenameFileNameValid = props?.setRenameFileNameValid;
    const setLoading = props?.setLoading;
    const loading = props?.loading;
    
    const { token } = useAuth();
    const { showSnackbar } = useSnackbar();

    const [newFileName, setNewFileName] = useState("");
    const { renameImage } = useSimplicityImage();
    const imageEditContext = useContext(ImageEditContext);
    const selectedImage = imageEditContext?.selectedImage;
    const setSelectedImage = imageEditContext?.setSelectedImage;
    const openRename = imageEditContext?.openRename;
    const setOpenRename = imageEditContext?.setOpenRename;
    const isArchiving = imageEditContext?.isArchiving;
    const isReplacing = imageEditContext?.isReplacing;
    const setHasChanges = imageEditContext?.setHasChanges;

    const handleReload = async (image) => {
        Logger.debug("[ImageEditDialog] onReload...");
        setSelectedImage(image);
    };
    const handleOpenRename = () => {
        setFileNameExist(false);
        setRenameFileNameValid(true);
        setOpenRename(true);
        setNewFileName(selectedImage?.file_name || "");
    };

    const handleFilenameChange = (event) => {
        const value = event.target.value;
        setFileNameExist(false);
        setRenameFileNameValid(true);
        setNewFileName(value);
    };

    const saveFilename = async () => {
        setLoading(true);
        try {
            const { exist } = await SimpliCityAdminSDK.image.fileExist(token, newFileName);
            if (exist) {
                setLoading(false);
                return setFileNameExist(exist);
            }
            const image = await renameImage(selectedImage?.guid, newFileName);
            if (image) {
                setSelectedImage(image);
                showSnackbar("Image Renamed.");
                setOpenRename(false);
                setHasChanges(true);
                setRenameFileNameValid(true);
                handleReload(image);
            } else {
                setRenameFileNameValid(false);
                showSnackbar("Document Rename Failed");
            }
        } catch (error) {
            setLoading(false);
            setHasChanges(false);
            // Check if the rename file name is invalid
            if (error.response?.data?.message === "InvalidRenameFileName") {
                setRenameFileNameValid(true);
            }
            showSnackbar("Failed to rename image.");
        } finally {
            setLoading(false);
        }
    };

    const cancelEdit = () => {
        setFileNameExist(false);
        setRenameFileNameValid(true);
        setOpenRename(false);
        setNewFileName(selectedImage?.file_name || "");
    };
    
    return (
        <div className="container">
            <div className="file-row">
                <div className="filename">Filename</div>
            </div>
            <TextFieldEditor
                containerClassName={"file-name-container"}
                currentTextValue={selectedImage?.file_name}
                editMode={openRename}
                handleCancelEdit={cancelEdit}
                handleOpenEditor={handleOpenRename}
                handleSave={saveFilename}
                handleTextValueChange={handleFilenameChange}
                loading={loading}
                newTextValue={newFileName}
                showEditButton={!isArchiving && !isReplacing}
                fileNameExist={fileNameExist}
                renameFileNameValid={renameFileNameValid}
                // hideDisplay={true}
            />
        </div>
    );
};

export { ImageRenamePanel };
